import { gql } from '@apollo/client';
export const USER_MEMBERSHIP_FULFILLMENTS = gql `
  query UserMembershipFulfillments($id: ID!) {
    user(id: $id) {
      id
      memberships {
        id
        tier {
          id
          name
          business {
            alias
            name
          }
          discounts {
            scope
            percentage
          }
        }
        fulfillments(query: { isFulfilled: false }) {
          id
          fulfillmentDate
          variant {
            description
          }
          addons {
            description
            additionalCost {
              amount
            }
          }
          benefit {
            id
            name
          }
          address {
            line1
            line2
            city
            state
            zip
            country
          }
        }
      }
    }
  }
`;
export const MARK_FULFILLMENT_FULFILLED = gql `
  mutation FulfillmentMarkFulfilled($id: ID!) {
    fulfillmentMarkFulfilled(id: $id) {
      id
      isFulfilled
    }
  }
`;
export const BATCH_MARK_FULFILLMENT_FULFILLED = gql `
  mutation FulfillmentBatchMarkFulfilled($query: FulfillmentQuery!) {
    fulfillmentBatchMarkFulfilled(query: $query) {
      id
      isFulfilled
    }
  }
`;
export const GET_MEMBERSHIP = gql `
  query GetMembership($membershipId: ID!, $fulfillmentQuery: FulfillmentQuery) {
    membership(id: $membershipId) {
      id
      user {
        id
        firstName
        lastName
        emailAddress
        phoneNumber
      }
      tier {
        id
        name
        benefits {
          ...on SubscriptionBenefit {
            id
            name
            delivery
          }
        }
      }
      fulfillments(query: $fulfillmentQuery) {
        id
        isFulfilled
        fulfillmentDate
        benefit {
          name
        }
        address {
          line1
          line2
          city
          state
          zip
          country
        }
        shippingNotes
        addons {
          id
          description
        }
        variant {
          id
          description
        }
      }
    }
  }
`;
// Get a list of total fulfillments by customer
// Scoped to the business
export const BUSINESS_FULFILLMENTS = gql `
  query BusinessFulfillments($id: ID!, $query: FulfillmentQuery) {
    business(id: $id) {
      id
      name
      members {
        id
        user {
          id
          firstName
          lastName
          emailAddress
          phoneNumber
        }
        tier {
          id
          name
          benefits {
            ...on SubscriptionBenefit {
              id
              name
              delivery
            }
          }
        }
        fulfillments(query: $query) {
          id
          isFulfilled
          isGift
          fulfillmentDate
          benefit {
            name
          }
          address {
            line1
            line2
            city
            state
            zip
            country
          }
          shippingNotes
          addons {
            id
            description
          }
          variant {
            id
            description
          }
        }
      }
    }
  }
`;
export const CREATE_FULFILLMENT = gql `
  mutation CreateFulfillment($membershipTierId: ID!, $userId: ID!) {
    fulfillmentCreate(membershipTierId: $membershipTierId, userId: $userId) {
      id
    }
  }
`;
export const ADDON_FRAGMENT = gql `
  fragment AddonFragment on Addon {
    id
    description
    applyDiscount
    dontRollOver
    platformId
    platformName
    additionalCost {
      amount
    }
  }
`;
export const VARIANT_FRAGMENT = gql `
  fragment VariantFragment on Variant {
    id
    description
    additionalCost {
      amount
    }
    platformName
    platformId
    curationExcludeProducts
    curationIncludeVariants
    curationSearchTerms
    curationStatus
  }
`;
export const BENEFIT_FRAGMENT = gql `
  fragment BenefitFragment on Benefit {
    __typename
    id
    name
    ...on SubscriptionBenefit {
      delivery
      isAddonOnly
      nextShipmentDate
      shippingFrequency {
        number
        period
      }
      deliveryFrequency
      firstShipmentDelay
      curationPreferOverstock
      curationMatchMode
      products {
        id
        name
        platformId
        platformName
        platformVariantsOnlyMode
        variants {
          ...VariantFragment
        }
        curationValue
        curationCollections
        curationExcludeProducts
      }
      variants {
        ...VariantFragment
      }
      addons {
        ...AddonFragment
      }
      membershipTiers {
        id
        name
      }
    }
  }
  ${ADDON_FRAGMENT}
  ${VARIANT_FRAGMENT}
`;
export const BENEFIT = gql `
  query GetBenefit($id: ID!) {
    benefit(id: $id) {
      id
      name
    }
  }
`;
export const SUBSCRIPTION_BENEFIT = gql `
  query GetSubscriptionBenefit($id: ID!) {
    benefit(id: $id) {
      ...BenefitFragment
    }
  } 
  ${BENEFIT_FRAGMENT}
`;
// Updates the benefit and returns some data
export const UPDATE_BENEFIT = gql `
  mutation UpdateBenefit($id: ID!, $benefit: BenefitInput!) {
    updateBenefit(id: $id, benefit: $benefit) {
      ...BenefitFragment
    }
  } 
  ${BENEFIT_FRAGMENT}
`;
export const LAUNCH_BUSINESS = gql `
  mutation LaunchBusiness ($id: ID!) {
    launchBusiness(id: $id) {
      success
      error
      message
    }
  }
`;
export const UPDATE_SQUARESPACE_SHOP = gql `
  mutation UpdateSquarespaceShop($id: ID!, $squarespaceShop: String) {
    updateBusiness(id: $id, business: {
      squarespaceShop: $squarespaceShop
    }) {
      id
      squarespaceShop
    }
  }
`;
export const CREATE_SHOPIFY_APP = gql `
  mutation CreateShopify($id: ID, $name: String, $shopifyShop: String) {
    createShopifyApp(id: $id, name: $name, shopifyShop: $shopifyShop)
  }
`;
export const GET_INSTALL_LINK = gql `
  query GetInstallLink($id: ID!) {
    business(id: $id) {
      id
      shopifyApp {
        installLink
      }
    }
  }
`;
export const GET_INSTALL_LINK_BY_SHOPIFY_SHOP = gql `
  query GetInstallLinkByShopifyShop($shopifyShop: String!) {
    shopifyApp(shopifyShop: $shopifyShop) {
      installLink
    }
  }
`;
export const CREATE_BLANK_SIMPLE_BENEFIT = gql `
  mutation CreateBlankSimpleBenefit($membershipTier: ID!) {
    addBenefit(membershipTierId: $membershipTier, benefit: {
      regular: {
        name: "A fun perk with every visit!"
      }
    }) {
      id
      name
    }
  }
`;
export const CREATE_BLANK_SUBSCRIPTION_BENEFIT = gql `
  mutation CreateBlankSubscriptionBenefit($membershipTier: ID!) {
    addBenefit(membershipTierId: $membershipTier, benefit: {
      subscription: {
        name: "Receive a surprise!"
        delivery: QUARTERLY
      }
    }) {
      id
      name
      ...on SubscriptionBenefit {
        delivery
      }
    }
  }
`;
export const MEMBERSHIP_TIER_FRAGMENT = gql `
  fragment MembershipTierFragment on MembershipTier {
    id
    name
    manualDiscountCode
    defaultMode
    plans {
      kind
      price {
        amount
      }
    }
    discounts {
      scope
      percentage
      guests
    }
    image {
      id
      url
      cdnPath
    }
    benefits {
      ...BenefitFragment
    }
  }
  ${BENEFIT_FRAGMENT}
`;
export const UPDATE_MEMBERSHIP = gql `
  mutation UpdateMembership($id: ID!, $membershipInput: MembershipTierInput) {
    updateOrCreateMembership(businessId: $id, membershipInput: $membershipInput) {
      ...MembershipTierFragment
    }
  }
  ${MEMBERSHIP_TIER_FRAGMENT}
`;
export const MEMBERSHIP_TIER = gql `
  query MembershipTierQuery($id: ID!) {
    membershipTier(id: $id) {
      ...MembershipTierFragment
    }
  }
  ${MEMBERSHIP_TIER_FRAGMENT}
`;
export const UPDATE_MEMBERSHIP_TIER_IMAGE = gql `
  mutation UpdateMembershipTierImage($membershipTierId: ID!, $imageInput: ImageInput!) {
    updateMembershipTierImage(membershipTierId: $membershipTierId, imageInput: $imageInput) {
      ok
    }
  }
`;
export const BUSINESS_FRAGMENT = gql `
  fragment BusinessFragment on Business {
    id
    name
    alias
    status
    oneLiner
    mission
    currencySymbol
    currencyCode
    qualifierOrderValue
    qualifierCustomers
    stripeId
    enableEmailUpsell
    enableSmsUpsell
    enablePastCustomerUpsell
    enableRedirectUpsell
    enableTips
    address {
      neighborhood
      city
      stateAbbreviated
    }
    coverPhoto {
      id
      url
      cdnPath
    }
    logo {
      id
      url
      cdnPath
    }
    platforms {
      name
      mode
      connected
    }
    features {
      name
      completed
    }
    membershipTiers(kind:Live) { 
      ...MembershipTierFragment
    }
  }
  ${BENEFIT_FRAGMENT}
  ${MEMBERSHIP_TIER_FRAGMENT}
`;
export const UPDATE_BUSINESS_FRAGMENT = gql `
  fragment UpdateBusinessFragment on Business {
    id
    oneLiner
    mission
    enableEmailUpsell
    enableSmsUpsell
    enablePastCustomerUpsell
    enableRedirectUpsell
    enableTips
    shopifyShop
    shopifyVersion
  }
`;
export const BUSINESS = gql `
  query BusinessQuery($id: ID!) {
    business(id: $id) {
      ...BusinessFragment
    }
  }
  ${BUSINESS_FRAGMENT}
`;
gql `
  fragment UpdateBusiness on Business {
    ...UpdateBusinessFragment
  }
`;
export const UPDATE_BUSINESS = gql `
  mutation UpdateBusiness($id: ID!, $business: BusinessInput!) {
    updateBusiness(id: $id, business: $business) {
      ...UpdateBusinessFragment
    }
  }
  ${UPDATE_BUSINESS_FRAGMENT}
`;
export const UPDATE_COVER_PHOTO = gql `
  mutation UpdateCoverPhoto($businessId: ID!, $imageInput: ImageInput!) {
    updateCoverPhoto(businessId: $businessId, imageInput: $imageInput) {
      id
      coverPhoto {
        id
        url
        cdnPath
      }
    }
  }
`;
export const UPDATE_LOGO = gql `
  mutation UpdateLogo($businessId: ID!, $imageInput: ImageInput!) {
    updateLogo(businessId: $businessId, imageInput: $imageInput) {
      id
      logo {
        id
        url
        cdnPath
      }
    }
  }
`;
export const CUSTOMER_ANALYTICS_REPORT = gql `
  query CustomerAnalyticsReport($id: ID!) {
    business(id: $id) {
      id
      reports(reports: ["CustomerAnalyticsReport"]) {
        title
        description
        ...on CustomerAnalyticsReport {
          complete
          averageOrderValue {
            amount
          }
          customerVisitFrequency
          customerData {
            totalCustomers
            totalCustomersWithEmail
            totalCustomersWithSMS
          }
        }
      }
    }
  }
`;
